/**
 * Comments Overlay Component
 *
 * Renders comment markers on PDF pages.
 * Handles click to view comment in sidebar and adding new comments.
 * Supports dragging markers to reposition them.
 */

import React, { useCallback, useState, useRef } from 'react';
import styles from './CommentsOverlay.module.scss';
import type { Comment } from './CommentsSidebar';

export interface CommentsOverlayProps {
  comments: Comment[];
  pageNum: number;
  pageWidth: number;
  pageHeight: number;
  zoom: number;
  onCommentClick: (comment: Comment) => void;
  onUpdateComment?: (commentId: string, updates: Partial<Comment>) => void;
  onAddCommentAt?: (pageNum: number, x: number, y: number) => void;
  addCommentMode?: boolean; // True when "Add Comment" tool is active
}

export const CommentsOverlay: React.FC<CommentsOverlayProps> = ({
  comments,
  pageNum,
  pageWidth,
  pageHeight,
  zoom,
  onCommentClick,
  onUpdateComment,
  onAddCommentAt,
  addCommentMode = false,
}) => {
  // Filter comments for this page - only show markers for parent comments (not replies)
  // Replies are threaded in the sidebar and share the parent's marker position
  const pageComments = comments.filter(c => c.pageNum === pageNum && !c.parentId);

  // Drag state
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const dragStartPos = useRef<{ x: number; y: number; commentX: number; commentY: number } | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Base marker size (in PDF coordinates, will be scaled by zoom)
  const baseMarkerSize = 30;

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!addCommentMode || !onAddCommentAt) return;

      // Get click position relative to overlay (screen coordinates)
      const rect = e.currentTarget.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;

      // Convert to PDF coordinates
      const pdfX = screenX / zoom;
      const pdfY = screenY / zoom;

      // Position marker so cursor is at bottom-left (marker is baseMarkerSize in PDF coords)
      const adjustedY = pdfY - baseMarkerSize;

      onAddCommentAt(pageNum, pdfX, Math.max(0, adjustedY));
    },
    [addCommentMode, onAddCommentAt, pageNum, zoom, baseMarkerSize]
  );

  const handleMarkerMouseDown = useCallback(
    (e: React.MouseEvent, comment: Comment) => {
      e.stopPropagation();
      e.preventDefault();

      if (addCommentMode || !overlayRef.current) return;

      const rect = overlayRef.current.getBoundingClientRect();
      dragStartPos.current = {
        x: e.clientX,
        y: e.clientY,
        commentX: comment.x,
        commentY: comment.y,
      };
      setDraggingId(comment.id);
    },
    [addCommentMode]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!draggingId || !dragStartPos.current || !overlayRef.current || !onUpdateComment) return;

      // Calculate delta in screen pixels
      const deltaX = e.clientX - dragStartPos.current.x;
      const deltaY = e.clientY - dragStartPos.current.y;

      // Convert to PDF coordinates
      const pdfDeltaX = deltaX / zoom;
      const pdfDeltaY = deltaY / zoom;

      let newX = dragStartPos.current.commentX + pdfDeltaX;
      let newY = dragStartPos.current.commentY + pdfDeltaY;

      // Clamp to page bounds (account for marker size in PDF coordinates)
      newX = Math.max(0, Math.min(pageWidth / zoom - baseMarkerSize, newX));
      newY = Math.max(0, Math.min(pageHeight / zoom - baseMarkerSize, newY));

      onUpdateComment(draggingId, { x: newX, y: newY });
    },
    [draggingId, pageWidth, pageHeight, zoom, baseMarkerSize, onUpdateComment]
  );

  const handleMouseUp = useCallback(() => {
    if (draggingId) {
      setDraggingId(null);
      dragStartPos.current = null;
    }
  }, [draggingId]);

  // Add/remove global mouse event listeners for drag
  React.useEffect(() => {
    if (draggingId) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [draggingId, handleMouseMove, handleMouseUp]);

  return (
    <div
      ref={overlayRef}
      className={`${styles.overlay} ${addCommentMode ? styles.addMode : ''}`}
      style={{ width: pageWidth, height: pageHeight }}
      onClick={handleOverlayClick}
    >
      {pageComments.map(comment => {
        const markerSize = baseMarkerSize * zoom;
        return (
          <div
            key={comment.id}
            className={`${styles.commentMarker} ${draggingId === comment.id ? styles.dragging : ''}`}
            style={{
              left: `${comment.x * zoom}px`,
              top: `${comment.y * zoom}px`,
              width: `${markerSize}px`,
              height: `${markerSize}px`,
              fontSize: `${markerSize * 0.6}px`, // Scale icon size proportionally
            }}
            onMouseDown={(e) => handleMarkerMouseDown(e, comment)}
            onClick={(e) => {
              e.stopPropagation();
              // Only trigger click if not dragging
              if (!draggingId) {
                onCommentClick(comment);
              }
            }}
            title={`${comment.author}: ${comment.text.substring(0, 100)}${comment.text.length > 100 ? '...' : ''}`}
          >
            {/* Comment icon using SVG with fill */}
            <svg
              viewBox="0 0 24 24"
              style={{ width: '100%', height: '100%' }}
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
        );
      })}
    </div>
  );
};
